import type { EmbedConfig } from "../../search/rules/params.js";
import type { EmbedContent } from "../stores/embedContent.js";
import { clampConfigToAllowlists } from "./embedAllowlist.js";
import { clampConfigToFieldDependencies } from "./fieldDependencies.js";
import { clampConfigToSafetyRules, type EmbedSafetyRules } from "./safetyRules.js";

/**
 * clampEmbedConfigToSecurity narrows a resolved embed config to what the organization's security
 * boundary permits, so neither a URL param, a default_prop nor a runtime `mountaya:setItinerary`
 * override surfaces a capability the organization withheld from its embeds, and the result is a
 * config the embed can render as it stands.
 *
 * It applies, in order: {@link clampConfigToSafetyRules}, which forces every safety-restricted
 * boolean (the overlay time slider, the profile's analysis and elevation) off;
 * {@link clampConfigToAllowlists}, which holds the active overlay, the enrichments and the
 * overlays panel to the enforced cap `safety_rules.with_overlays` and to the embed's own display
 * lists; and {@link clampConfigToFieldDependencies}, which switches a field off while a field it
 * needs is off, whichever of the three hid it. Run it on the initial load and again whenever a
 * host override is folded over the config ({@link applyEmbedOverrides}), so the same boundary
 * holds for every switch.
 *
 * @param config The resolved embed config.
 * @param safetyRules The organization's `settings.safety_rules` slice, or undefined when it
 *   carries none.
 */
export const clampEmbedConfigToSecurity = (
  config: EmbedConfig,
  safetyRules: EmbedSafetyRules | undefined,
): EmbedConfig =>
  clampConfigToFieldDependencies(
    clampConfigToAllowlists(
      clampConfigToSafetyRules(config, safetyRules),
      safetyRules?.with_overlays,
    ),
  );

/**
 * applyEmbedOverrides folds a host's live content command (the itinerary id plus the
 * accumulated display overrides an inbound `mountaya:setItinerary` carries) over the
 * route's resolved embed config, producing the config the surface renders. It replaces
 * the itinerary id, with null where the command names the base map alone, and applies ONLY
 * the override fields the host actually provided: an omitted field, or a null
 * overlay/terrain list, leaves the resolved value in place, as the contract states (an
 * omitted field leaves the current state unchanged). The override keys map
 * one-to-one onto EmbedConfig, and it touches only that overridable subset (terrain, the
 * overlay/terrain allowlists, and the control toggles); the single active overlay, the
 * terrain tint, the content ids other than the itinerary, the preferences, and the
 * enrichments stay as the route resolved them. The caller clamps the result again through
 * {@link clampEmbedConfigToSecurity}, so a host can never lift a capability past the
 * organization's security boundary this way.
 *
 * @param config The embed config the route resolved.
 * @param content The host's latest content command, as the embed-content slot holds it.
 * @returns A new config; `config` is left as it was.
 */
export const applyEmbedOverrides = (config: EmbedConfig, content: EmbedContent): EmbedConfig => {
  const { itineraryId, overrides } = content;
  const next: EmbedConfig = { ...config, itineraryId };

  if (overrides.terrain !== undefined) {
    next.terrain = overrides.terrain;
  }

  if (Array.isArray(overrides.withOverlays)) {
    next.withOverlays = overrides.withOverlays;
  }

  if (Array.isArray(overrides.withTerrains)) {
    next.withTerrains = overrides.withTerrains;
  }

  if (overrides.withDirectionsControl !== undefined) {
    next.withDirectionsControl = overrides.withDirectionsControl;
  }

  if (overrides.withDownloadControl !== undefined) {
    next.withDownloadControl = overrides.withDownloadControl;
  }

  if (overrides.withNavigationControl !== undefined) {
    next.withNavigationControl = overrides.withNavigationControl;
  }

  if (overrides.withOverlaysControl !== undefined) {
    next.withOverlaysControl = overrides.withOverlaysControl;
  }

  if (overrides.withPreferencesControl !== undefined) {
    next.withPreferencesControl = overrides.withPreferencesControl;
  }

  if (overrides.withProfileAnalysis !== undefined) {
    next.withProfileAnalysis = overrides.withProfileAnalysis;
  }

  if (overrides.withProfileCharacteristics !== undefined) {
    next.withProfileCharacteristics = overrides.withProfileCharacteristics;
  }

  if (overrides.withProfileElevation !== undefined) {
    next.withProfileElevation = overrides.withProfileElevation;
  }

  if (overrides.withProfilePaceControl !== undefined) {
    next.withProfilePaceControl = overrides.withProfilePaceControl;
  }

  if (overrides.withStreetviewControl !== undefined) {
    next.withStreetviewControl = overrides.withStreetviewControl;
  }

  return next;
};
